import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import {
    SettingType,
    Widget,
    CodeBlock,
    Define,
    BlockType,
} from '@appblocks/codegeneration/types';
import BaseWidget, { sizes } from '@appblocks/codegeneration/Widgets/BaseWidget';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';

export default class LCDGraphWidget extends BaseWidget {
    constructor() {
        super();
        this.name = 'Line Graph';
        this.id = 'lcd_graph_widget';
        this.minW = sizes[this.id].minW;
        this.minH = sizes[this.id].minH;

        const settings = super.getWidgetSettings(['NAME', 'LABEL', 'BACKGROUND_COLOR', 'COLOR', 'DISPLAY_LABEL', 'FILL_COLOR', 'MIN', 'MAX'])
            .concat([
                {
                    id: 'MAX_POINTS',
                    name: 'Max Points',
                    settingType: SettingType.NUMBER,
                    required: true,
                    default: 30,
                    min: 1,
                    max: 50,
                },
            ]);

        this.settings = this.settings.concat(settings);
    }


    getDependencies() {
        const dependencies = [
            'lcd',
        ];
        return dependencies;
    }

    getFiles(): Array<string> {
        return [
            'libraries/lcd/lcd_graph.tbh',
            'libraries/lcd/lcd_graph.tbs',
        ];
    }

    getCodeBlocks(): CodeBlock[] {
        const min = this.getSettingValue('MIN');
        const max = this.getSettingValue('MAX');
        const blocks: CodeBlock[] = [];

        const project = this.project;

        const SCREEN = project.getParent(this);
        const displayFeature = project.getParent(SCREEN);
        const screens = displayFeature.getSettingValue('SCREENS');
        let LCD_GRAPH_WIDGETS_COUNT = 0;
        let LCD_GRAPH_MAX_POINTS = 30;
        const graphWidgets: LCDGraphWidget[] = [];

        for (let j = 0; j < screens.length; j++) {
            const screen = screens[j];
            const widgets = screen.getSettingValue('WIDGETS');
            for (let i = 0; i < widgets.length; i++) {
                const widget = widgets[i];
                const type = widget.id;
                switch (type) {
                    case 'lcd_graph_widget':
                        graphWidgets.push(widget);
                        LCD_GRAPH_WIDGETS_COUNT++;
                        break;
                }
            }
        }

        for (let i = 0; i < graphWidgets.length; i++) {
            const widget = graphWidgets[i];

            const current_LCD_GRAPH_MAX_POINTS = widget.getSettingValue('MAX_POINTS');
            if (current_LCD_GRAPH_MAX_POINTS > LCD_GRAPH_MAX_POINTS) {
                LCD_GRAPH_MAX_POINTS = current_LCD_GRAPH_MAX_POINTS;
            }
        }

        for (let i = 0; i < graphWidgets.length; i++) {
            const widget = graphWidgets[i];
            if (widget.getID() !== this.getID()) {
                continue;
            }
            const widgetObject = widget.getWidgetObject();

            blocks.push({
                location: 'lcd_init_widgets',
                blockType: BlockType.CODE,
                template: `
    lcd_graph_widgets(${i}).x_pos=${widgetObject.x}
    lcd_graph_widgets(${i}).y_pos=${widgetObject.y}
    lcd_graph_widgets(${i}).width=${widgetObject.w}
    lcd_graph_widgets(${i}).height=${widgetObject.h}
    lcd_graph_widgets(${i}).visible=${widget.getSettingValue('VISIBLE') === '1' ? 'true' : 'false'}
    lcd_graph_widgets(${i}).screen=${widgetObject.screen}
    lcd_graph_widgets(${i}).min_val=${min}
    lcd_graph_widgets(${i}).max_val=${max}
    lcd_graph_widgets(${i}).current_index=0
    lcd_graph_widgets(${i}).background_color=${PlatformUtils.hexToTibboColor(widgetObject.backgroundColor)}
    lcd_graph_widgets(${i}).foreground_color=${PlatformUtils.hexToTibboColor(widgetObject.color)}
    lcd_graph_widgets(${i}).fill_color=${PlatformUtils.hexToTibboColor(widgetObject.fillColor)}
    lcd_graph_widgets(${i}).max_points=${widget.getSettingValue('MAX_POINTS')}
    `,
            });
        }

        blocks.push({
            location: 'GLOBAL_DEFINES',
            blockType: BlockType.CODE,
            id: 'defineLCD_GRAPH_WIDGETS_COUNT',
            template: `
#define LCD_GRAPH_WIDGETS_COUNT ${LCD_GRAPH_WIDGETS_COUNT}
#define LCD_GRAPH_MAX_POINTS ${LCD_GRAPH_MAX_POINTS}`,
        });

        blocks.push({
            location: 'GLOBAL_DECLARATIONS',
            blockType: BlockType.CODE,
            id: 'declare lcd_graph_widgets as lcd_graph_widget(LCD_GRAPH_WIDGETS_COUNT)',
            template: `
declare lcd_graph_widgets as lcd_graph_widget(LCD_GRAPH_WIDGETS_COUNT)`,
        });

        blocks.push({
            location: 'GLOBAL_DIMS',
            blockType: BlockType.CODE,
            id: 'dim lcd_graph_widgets as lcd_graph_widget(LCD_GRAPH_WIDGETS_COUNT)',
            template: `
dim lcd_graph_widgets as lcd_graph_widget(LCD_GRAPH_WIDGETS_COUNT)`,
        });

        return blocks;
    }
}
